import React, { useState, useEffect } from 'react';
import { logAdminAction } from '../../lib/adminAuth';
import { resendEmailService } from '../../lib/resendEmailService';
import { getSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface ConversionRequest {
  id: string;
  user_id: number;
  shares_requested: number;
  usdt_amount: number;
  share_price: number;
  phase_id: number;
  phase_number: number;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  approved_at?: string;
  rejected_at?: string;
  admin_notes?: string;
  approved_by_admin_id?: number;
  users: {
    id: number;
    username: string;
    full_name: string;
    email: string;
  };
}

interface CommissionConversionManagerProps {
  currentUser: any;
}

export const CommissionConversionManager: React.FC<CommissionConversionManagerProps> = ({
  currentUser
}) => {
  const [conversionRequests, setConversionRequests] = useState<ConversionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<ConversionRequest | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');

  useEffect(() => {
    loadConversionRequests();
  }, []);

  const loadConversionRequests = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/commission-operations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_conversion_requests' })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      setConversionRequests(result.data || []);
    } catch (error) {
      console.error('Error loading conversion requests:', error);
      alert('Failed to load conversion requests');
    } finally {
      setLoading(false);
    }
  };

  const handleActionClick = (request: ConversionRequest, action: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setActionType(action);
    setAdminNotes('');
    setShowModal(true);
  };

  const processConversionRequest = async () => {
    if (!selectedRequest) return;

    setProcessingId(selectedRequest.id);
    try {
      if (actionType === 'approve') {
        await approveConversion(selectedRequest);
      } else {
        await rejectConversion(selectedRequest);
      }

      await loadConversionRequests();
      setShowModal(false);
      setSelectedRequest(null);
      setAdminNotes('');
    } catch (error) {
      console.error('Error processing conversion:', error);
      alert(`Failed to ${actionType} conversion: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setProcessingId(null);
    }
  };

  const approveConversion = async (request: ConversionRequest) => {
    console.log(`🔄 Processing conversion approval for user ${request.user_id}: $${request.usdt_amount} → ${request.shares_requested} shares`);

    // 1. Call API to approve conversion and update balances
    const approveResponse = await fetch(`${API_BASE_URL}/api/admin/commission-operations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
      body: JSON.stringify({
        operation: 'approve_conversion_full',
        data: {
          requestId: request.id,
          adminId: currentUser?.database_user?.id,
          usdtAmount: request.usdt_amount,
          sharesRequested: request.shares_requested,
          userId: request.user_id
        }
      })
    });
    const approveResult = await approveResponse.json();
    if (!approveResponse.ok) throw new Error(approveResult.error);

    // Referral commission is paid once inside approve_conversion_full.
    // A second call here would pay the sponsor again on the 15/15 path.

    // 3. Log admin action
    await logAdminAction(currentUser?.database_user?.email || 'unknown', 'conversion_approved', 'conversion', request.id, {
      conversionId: request.id, userId: request.user_id, usdtAmount: request.usdt_amount,
      sharesApproved: request.shares_requested, adminNotes: adminNotes || 'No notes provided', commissionProcessed: true
    });

    // 4. Send conversion approval email via centralized API
    try {
      console.log('📧 [EmailService] Sending conversion approval email...');
      const userResponse = await fetch(`${API_BASE_URL}/api/admin/commission-operations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'get_user_by_id', data: { userId: request.user_id } })
      });
      const userResult = await userResponse.json();
      const userData = userResult.data;

      if (userData?.email) {
        const emailResponse = await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({
            emailType: 'conversion_approval',
            data: { email: userData.email, fullName: userData.full_name || userData.username, usdtAmount: request.usdt_amount,
              sharesReceived: request.shares_requested, sharePrice: request.share_price, dashboardUrl: `${window.location.origin}/dashboard` }
          })
        });
        if (emailResponse.ok) console.log('✅ [EmailService] Conversion approval email sent successfully');
        else console.error('❌ [EmailService] Failed to send conversion approval email:', await emailResponse.text());
      }
    } catch (emailError) {
      console.error('❌ [EmailService] Error sending conversion approval email:', emailError);
    }

    console.log(`✅ Conversion approved: $${request.usdt_amount} → ${request.shares_requested} shares for user ${request.user_id}`);
  };

  const rejectConversion = async (request: ConversionRequest) => {
    // 1. Call API to reject conversion and refund USDT
    const rejectResponse = await fetch(`${API_BASE_URL}/api/admin/commission-operations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
      body: JSON.stringify({
        operation: 'reject_conversion_full',
        data: {
          requestId: request.id,
          adminId: currentUser?.database_user?.id,
          adminNotes,
          userId: request.user_id,
          usdtAmount: request.usdt_amount
        }
      })
    });
    const rejectResult = await rejectResponse.json();
    if (!rejectResponse.ok) throw new Error(rejectResult.error);

    // 2. Log admin action
    await logAdminAction(currentUser?.database_user?.email || 'unknown', 'conversion_rejected', 'conversion', request.id, {
      conversionId: request.id, userId: request.user_id, usdtRefunded: request.usdt_amount, rejectionReason: adminNotes
    });

    // 3. Send conversion rejection email via centralized API
    try {
      console.log('📧 [EmailService] Sending conversion rejection email...');
      const userResponse = await fetch(`${API_BASE_URL}/api/admin/commission-operations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'get_user_by_id', data: { userId: request.user_id } })
      });
      const userResult = await userResponse.json();
      const userData = userResult.data;

      if (userData?.email) {
        const emailResponse = await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({
            emailType: 'conversion_rejection',
            data: { email: userData.email, fullName: userData.full_name || userData.username, usdtAmount: request.usdt_amount,
              rejectionReason: adminNotes || 'No reason provided', dashboardUrl: `${window.location.origin}/dashboard?tab=commissions` }
          })
        });
        if (emailResponse.ok) console.log('✅ [EmailService] Conversion rejection email sent successfully');
        else console.error('❌ [EmailService] Failed to send conversion rejection email:', await emailResponse.text());
      }
    } catch (emailError) {
      console.error('❌ [EmailService] Error sending conversion rejection email:', emailError);
    }

    console.log(`❌ Conversion rejected and refunded: $${request.usdt_amount} for user ${request.user_id}`);
  };

  /**
   * Process referral commissions for approved conversion requests
   * This treats the conversion as equivalent to a purchase for commission purposes
   */
  const processConversionCommissions = async (request: ConversionRequest, currentPhase: any) => {
    console.log(`💰 COMMISSION PROCESSING: Starting commission calculation for conversion ${request.id}`);

    try {
      // Import the bulletproof commission system
      const { CommissionSafeguardService } = await import('../../lib/services/commissionSafeguardService');

      // Process commission using the same system as regular purchases.
      // PHASE-E-2026-05-21: pass sourceType='conversion_admin' + the commission_conversions
      // row id so the cascade rows can be told apart from phantom-fraud rows. See
      // audit-reports/2026-05-21-root-cause-mlc-null-purchase-id.md.
      const commissionResult = await CommissionSafeguardService.processCommissionWithSafeguards({
        userId: request.user_id,
        amount: request.usdt_amount,
        shares: request.shares_requested,
        currentPhase: currentPhase,
        transactionId: undefined,
        sourceType: 'conversion_admin',
        sourceConversionId: request.id,
        adminProcessed: true
      });

      if (commissionResult.success) {
        console.log(`✅ COMMISSION SUCCESS: Processed commission for conversion ${request.id}`);
        console.log(`   • Referrer ID: ${commissionResult.referrerId}`);
        console.log(`   • USDT Commission: $${commissionResult.usdtCommission?.toFixed(2)}`);
        console.log(`   • Share Commission: ${commissionResult.shareCommission?.toFixed(4)} shares`);

        // Send notification to the sponsor about the commission via API
        await sendCommissionNotification(commissionResult.referrerId!, request, commissionResult);

        await logAdminAction(currentUser?.database_user?.email || 'unknown', 'conversion_commission_processed', 'conversion', request.id, {
          conversionId: request.id, referrerId: commissionResult.referrerId, usdtCommission: commissionResult.usdtCommission,
          shareCommission: commissionResult.shareCommission, commissionTransactionId: commissionResult.commissionTransactionId
        });
      } else {
        console.log(`ℹ️ COMMISSION INFO: ${commissionResult.error || 'No commission processed'}`);
        await logAdminAction(currentUser?.database_user?.email || 'unknown', 'conversion_commission_skipped', 'conversion', request.id, {
          conversionId: request.id, userId: request.user_id, reason: commissionResult.error, validationErrors: commissionResult.validationErrors
        });
      }
    } catch (error) {
      console.error('❌ COMMISSION ERROR: Failed to process commission for conversion:', error);
      throw error;
    }
  };

  /**
   * Send notification to sponsor about commission earned from conversion
   */
  const sendCommissionNotification = async (
    referrerId: number,
    request: ConversionRequest,
    commissionResult: any
  ) => {
    try {
      // Get referrer information via API
      const referrerResponse = await fetch(`${API_BASE_URL}/api/admin/commission-operations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'get_user_by_id', data: { userId: referrerId } })
      });
      const referrerResult = await referrerResponse.json();
      const referrer = referrerResult.data;

      if (!referrer) {
        console.warn('Could not find referrer for notification:', referrerId);
        return;
      }

      if (!referrer.email || referrer.email.trim() === '') {
        console.warn('❌ Referrer has no valid email address:', referrerId);
        return;
      }

      // Create in-app notification via API
      const notificationTitle = '💰 Commission Earned from Conversion';
      const notificationMessage = `You earned $${commissionResult.usdtCommission?.toFixed(2)} USDT + ${commissionResult.shareCommission?.toFixed(4)} shares commission from ${request.users.username}'s approved conversion of $${request.usdt_amount} to ${request.shares_requested} shares.`;

      const notifyResponse = await fetch(`${API_BASE_URL}/api/admin/commission-operations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'insert_notification',
          data: { userId: referrerId, title: notificationTitle, message: notificationMessage, type: 'commission' }
        })
      });
      if (notifyResponse.ok) console.log(`✅ In-app notification created for referrer ${referrer.username}`);
      else console.error('Failed to create commission notification');

      // Send email notification
      try {
        console.log(`📧 Sending commission email to ${referrer.email}...`);
        const emailResult = await resendEmailService.sendCommissionEarnedNotification({
          email: referrer.email,
          fullName: referrer.full_name || referrer.username,
          usdtCommission: commissionResult.usdtCommission || 0,
          shareCommission: commissionResult.shareCommission || 0,
          referredUserName: request.users.username,
          purchaseAmount: request.usdt_amount,
          transactionId: commissionResult.commissionTransactionId || request.id
        });
        if (emailResult.success) console.log(`✅ Commission email sent successfully to ${referrer.email}`);
        else console.error(`❌ Failed to send commission email: ${emailResult.error}`);
      } catch (emailError) {
        console.error('❌ Error sending commission email:', emailError);
      }
    } catch (error) {
      console.error('Error sending commission notification:', error);
    }
  };



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-400 bg-yellow-900/20';
      case 'approved': return 'text-green-400 bg-green-900/20';
      case 'rejected': return 'text-red-400 bg-red-900/20';
      default: return 'text-white bg-gray-900/20';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-white">Loading conversion requests...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Commission Conversion Management</h2>
        <button
          onClick={loadConversionRequests}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-2xl font-bold text-yellow-400">
            {conversionRequests.filter(r => r.status === 'pending').length}
          </div>
          <div className="text-sm text-white">Pending Requests</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-2xl font-bold text-green-400">
            {conversionRequests.filter(r => r.status === 'approved').length}
          </div>
          <div className="text-sm text-white">Approved</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-2xl font-bold text-red-400">
            {conversionRequests.filter(r => r.status === 'rejected').length}
          </div>
          <div className="text-sm text-white">Rejected</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-2xl font-bold text-blue-400">
            ${conversionRequests
              .filter(r => r.status === 'pending')
              .reduce((sum, r) => sum + r.usdt_amount, 0)
              .toFixed(2)}
          </div>
          <div className="text-sm text-white">Pending Value</div>
        </div>
      </div>

      {/* Conversion Requests Table */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">Conversion Requests</h3>
        </div>

        {conversionRequests.length === 0 ? (
          <div className="p-8 text-center text-white">
            No conversion requests found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                    USDT Amount
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                    Shares Requested
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                    Share Price
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {conversionRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-700/50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-white font-medium">
                        {request.users.username}
                      </div>
                      <div className="text-sm text-white">
                        {request.users.full_name || request.users.email}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-green-400">
                        ${request.usdt_amount.toFixed(2)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-yellow-400">
                        {request.shares_requested.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        ${request.share_price.toFixed(2)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${getStatusColor(request.status)}`}>
                        {request.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                      {formatDate(request.created_at)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      {request.status === 'pending' ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleActionClick(request, 'approve')}
                            disabled={processingId === request.id}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {processingId === request.id ? 'Processing...' : 'Approve'}
                          </button>
                          <button
                            onClick={() => handleActionClick(request, 'reject')}
                            disabled={processingId === request.id}
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <div className="text-gray-500">
                          {request.status === 'approved' ? 'Approved' : 'Rejected'}
                          {request.approved_at && (
                            <div className="text-sm">
                              {formatDate(request.approved_at)}
                            </div>
                          )}
                          {request.rejected_at && (
                            <div className="text-sm">
                              {formatDate(request.rejected_at)}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">
              {actionType === 'approve' ? 'Approve' : 'Reject'} Conversion Request
            </h3>

            <div className="space-y-3 mb-4">
              <div className="text-sm text-gray-300">
                <strong>User:</strong> {selectedRequest.users.username} ({selectedRequest.users.full_name})
              </div>
              <div className="text-sm text-gray-300">
                <strong>USDT Amount:</strong> ${selectedRequest.usdt_amount.toFixed(2)}
              </div>
              <div className="text-sm text-gray-300">
                <strong>Shares Requested:</strong> {selectedRequest.shares_requested.toLocaleString()}
              </div>
              <div className="text-sm text-gray-300">
                <strong>Share Price:</strong> ${selectedRequest.share_price.toFixed(2)}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Admin Notes {actionType === 'reject' ? '(Required)' : '(Optional)'}
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder={`Enter notes for ${actionType}ing this conversion...`}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={processConversionRequest}
                disabled={actionType === 'reject' && !adminNotes.trim()}
                className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                  actionType === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {actionType === 'approve' ? 'Approve' : 'Reject'} Conversion
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
